import type { ButtonHTMLAttributes, ReactNode } from "react";

import type { TactileIntent } from "@/shared/telegram";

/**
 * Figma: компонент `Button` (10:33), `Size=Large`.
 * Документация компонента в макете: «Primary, secondary, ghost and danger
 * actions. One primary action per screen.»
 *
 * Состояния `hover` в макете нет — WebView мобильный, наводить нечем.
 * Отклик на нажатие — утилита `press` (FE-13): масштаб `--motion-press` и
 * лёгкое затемнение. Варианта `Pressed` у Button в макете нет (он есть только
 * у IconButton), поэтому визуальный отклик собран из токенов движения, а не
 * снят с Figma.
 *
 * Тактильная часть отклика (FE-14) приходит от делегата на документе, а не
 * из `onClick`: кнопка лишь объявляет силу удара атрибутом `data-haptic`.
 * Она разная у вариантов не для красоты — `danger` стоит на необратимых
 * действиях (остановить робота), и он обязан ощущаться тяжелее, чем «дальше»,
 * а «дальше» — тяжелее, чем «отмена». Экраны подтверждения, где `primary`
 * списывает деньги, поднимают силу до `commit` пропом `haptic`.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

/**
 * Два размера, и оба взяты из макета — это не вкусовщина.
 *
 * `lg` — кнопка в потоке: скругление 16, паддинг по шкале.
 * `cta` — главное действие экрана: фиксированная высота `--control-button`.
 * `pill` — компактная кнопка пустого состояния.
 *
 * Три, а не одна, потому что в макете CTA экрана и кнопка в списке — разные
 * по высоте объекты; свести их к одной форме значило бы разойтись с
 * референсом на каждом экране.
 */
export type ButtonSize = "lg" | "cta" | "pill";

const SIZES: Record<ButtonSize, string> = {
  lg: "rounded-lg px-5 py-3.5 text-body",
  cta: "h-[var(--control-button)] rounded-md px-3.5 text-body",
  // Компактный CTA пустого состояния: в макете это 108×28 под текстом
  // «Событий пока нет» — полноширинная кнопка там перевесила бы сам текст.
  pill: "h-9 rounded-sm px-4 text-small",
};

/** Сила тактильного удара по варианту — шкала намерений из FE-14. */
const INTENTS: Record<ButtonVariant, TactileIntent> = {
  primary: "press",
  secondary: "tap",
  ghost: "tap",
  danger: "commit",
};

const VARIANTS: Record<ButtonVariant, { enabled: string; disabled: string }> = {
  primary: {
    // Заливка градиентом, а не плоским цветом: так нарисованы все акцентные
    // поверхности макета — CTA, активный таб периода, центральная «+».
    enabled:
      "bg-[image:var(--surface-accent)] border-transparent text-text-inverse shadow-[var(--shadow-inset-top)]",
    disabled: "bg-disabled-primary-bg border-transparent text-disabled-primary-text",
  },
  secondary: {
    enabled: "bg-surface-secondary border-border-strong text-text-primary",
    disabled: "bg-disabled-surface border-disabled-border text-disabled-text",
  },
  ghost: {
    enabled: "bg-[image:var(--surface-control)] border-border-subtle text-text-primary",
    disabled: "bg-background-raised border-disabled-ghost-border text-disabled-text",
  },
  danger: {
    enabled: "bg-surface-loss border-border-loss text-status-loss",
    disabled: "bg-disabled-danger-bg border-disabled-danger-border text-disabled-danger-text",
  },
};

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Растянуть на всю ширину контейнера — так CTA стоят на экранах состояний. */
  block?: boolean;
  submit?: boolean;
  /**
   * Ожидание ответа сервера. Подпись гаснет, на её месте — три точки, кнопка
   * перестаёт нажиматься. Без этого на время мутации CTA просто замирал, и
   * пользователь жал его повторно.
   */
  loading?: boolean;
  /**
   * Перебить силу отклика, назначенную варианту. Нужно там, где `primary`
   * стоит на необратимом действии: последний шаг покупки и вывода списывает
   * деньги и обязан ощущаться как `commit`, хотя выглядит обычным CTA.
   */
  haptic?: TactileIntent;
}

export function Button({
  children,
  variant = "primary",
  size = "lg",
  block = false,
  submit = false,
  disabled = false,
  loading = false,
  haptic,
  onClick,
  className,
  ...rest
}: ButtonProps) {
  const inactive = disabled || loading;
  const tone = VARIANTS[variant][inactive ? "disabled" : "enabled"];

  return (
    <button
      type={submit ? "submit" : "button"}
      disabled={inactive}
      aria-busy={loading || undefined}
      data-haptic={haptic ?? INTENTS[variant]}
      onClick={onClick}
      className={[
        "press relative flex items-center justify-center border border-solid font-semibold",
        SIZES[size],
        block ? "w-full" : "min-w-[150px]",
        tone,
        className ?? "",
      ].join(" ")}
      {...rest}
    >
      {/* Подпись не убирается из разметки: иначе кнопка на время запроса
          схлопнулась бы по ширине. */}
      <span
        className={[
          "flex items-center justify-center gap-2 transition-opacity duration-[var(--motion-fast)]",
          loading ? "opacity-0" : "opacity-100",
        ].join(" ")}
      >
        {children}
      </span>

      {loading ? <LoadingDots /> : null}
    </button>
  );
}

const DOTS = [0, 1, 2] as const;

function LoadingDots() {
  return (
    <span className="absolute inset-0 flex items-center justify-center gap-1" aria-hidden="true">
      {DOTS.map((index) => (
        <span
          key={index}
          className="animate-dot size-1.5 rounded-full bg-current"
          style={{ animationDelay: `calc(${String(index)} * var(--motion-fast))` }}
        />
      ))}
    </span>
  );
}
